"use client";

import { usePathname } from "next/navigation";
import { useLingui } from "@lingui/react/macro";
import { ChevronDownIcon, LanguagesIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DEFAULT_LOCALE, LOCALES, LOCALE_FLAGS, LOCALE_NAMES, isLocale, type Locale } from "@/lib/i18n/locales";
import { switchLocale } from "@/lib/i18n/switch-locale";
import { cn } from "@/lib/utils";

/**
 * The locale a public page is being read in, plus the switch.
 *
 * The whole of what a picker does, so the two shapes below cannot drift on the
 * part that matters — which locale counts as current, and what "picking the one
 * you are already in" means (nothing: `switchLocale` navigates, and a reload
 * would be a confusing answer to "I am already reading Swedish").
 *
 * `current` comes from the Lingui provider rather than a prop, which is what
 * lets both shapes drop into a server component without threading the locale
 * through it.
 */
const useLocalePicker = () => {
  const { i18n } = useLingui();
  const pathname = usePathname();
  const current: Locale = isLocale(i18n.locale) ? i18n.locale : DEFAULT_LOCALE;
  return {
    current,
    pick: (next: string) => {
      if (isLocale(next) && next !== current) switchLocale(next, pathname);
    },
  };
};

/**
 * Locale picker for a PUBLIC page's header.
 *
 * It sits in the header rather than the footer because of the redirect: someone
 * whose stated choice is Swedish is sent to `/sv/...` automatically, so the
 * first thing a visitor who lands in the wrong language needs is the way out —
 * and the footer is below the fold on every page we ship. At two locales an
 * inline footer list was fine; at three, the person who needs it most is the
 * one who cannot read the page well enough to go looking for it.
 *
 * A standalone dropdown rather than {@link LanguageToggle}'s submenu, because
 * here there is no menu to be a submenu OF — but deliberately the same CONTENT:
 * native names, each with a decorative flag. Two public controls that looked
 * different would read as two different settings.
 *
 * The trigger is QUIET — a globe, the current language's native name and a
 * chevron, in the muted text treatment the nav links already use. Deliberately
 * not a bordered box: `signInClass` next to it is one, so a boxed control put
 * the bar's two outlined buttons either side of the Sign up CTA and made the
 * language picker look like a third auth action. It is chrome, and should read
 * as chrome.
 *
 * `labelClassName` exists because the nav bar runs out of room before the
 * languages do. Measured signed-out at /pricing (logo + links + auth cluster +
 * gaps + padding): English needs 846px, German 1018px, Swedish 1037px — so at
 * `lg` (1024) the longest locale does not fit, and German clears it by 6px.
 * Folding the name away below `xl` takes ~58px off and puts Swedish at 979,
 * which leaves actual margin rather than a rounding error. Callers with room
 * (the legal header, /get-started) pass nothing and keep the name.
 *
 * Never a flag: a flag is a country and a locale is a language (see
 * LOCALE_FLAGS), so 🇬🇧 would be the one identifier this control must not use.
 * The globe claims no country, which is what lets it stand alone when the name
 * folds; the accessible name states the language either way. Flags stay where
 * the contract allows them — beside the names, in the menu.
 */
export function LanguageMenu({
  className = "",
  /** Tailwind visibility classes for the trigger's text label. Defaults to
   *  showing it; a caller in a bar that cannot spare the width folds it. */
  labelClassName = "",
}: { className?: string; labelClassName?: string }) {
  const { t } = useLingui();
  const { current, pick } = useLocalePicker();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t`Language: ${LOCALE_NAMES[current]}`}
        // `cn` so a caller can override the palette rather than append to it —
        // the legal pages hardcode cream/ink instead of the theme tokens, so
        // the default colours would be a dark-mode control on a light page.
        className={cn(
          "inline-flex h-10 items-center gap-1.5 text-sm font-medium text-foreground-muted transition hover:text-foreground",
          className,
        )}
      >
        <LanguagesIcon aria-hidden="true" className="size-4 shrink-0" />
        <span className={labelClassName}>{LOCALE_NAMES[current]}</span>
        {/* The affordance the border used to carry: without it, nothing else
            says this opens a menu rather than navigating. It also keeps the
            folded form legible as a control rather than a stray glyph. */}
        <ChevronDownIcon aria-hidden="true" className="size-3.5 shrink-0 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} aria-label={t`Language`}>
        <DropdownMenuRadioGroup value={current} onValueChange={pick}>
          {LOCALES.map((locale) => (
            <DropdownMenuRadioItem key={locale} value={locale}>
              <span aria-hidden="true" className="mr-2">{LOCALE_FLAGS[locale]}</span>
              {LOCALE_NAMES[locale]}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * The same picker for the phone-width menu, as a flat row instead of a
 * dropdown.
 *
 * Two reasons it is not just {@link LanguageMenu} in the sheet. A popover
 * opened from inside a sheet is a second overlay layered on the first, which is
 * the fiddliest thing a touch UI can ask of someone; and the sheet has vertical
 * room the header bar does not, so the argument for hiding the options behind a
 * trigger does not apply. Here every locale is one tap, on the surface where
 * the control is hardest to find in the first place.
 *
 * Stacked, not a row of pills: three native names side by side do not fit a
 * `max-w-xs` sheet, and the only way to make them fit would be to drop to flags
 * alone — the one identifier this control must never use (see LOCALE_FLAGS).
 */
export function LanguageChoices() {
  const { current, pick } = useLocalePicker();

  return (
    <div className="flex flex-col">
      {LOCALES.map((locale) => (
        <button
          key={locale}
          type="button"
          onClick={() => { pick(locale); }}
          aria-current={locale === current ? "true" : undefined}
          className={cn(
            "flex items-center gap-3 rounded-none px-2 py-2.5 text-left text-base transition",
            locale === current
              ? "font-medium text-foreground-strong"
              : "text-foreground-muted hover:text-foreground",
          )}
        >
          <span aria-hidden="true">{LOCALE_FLAGS[locale]}</span>
          {LOCALE_NAMES[locale]}
        </button>
      ))}
    </div>
  );
}
